import { RightSidebarHeader } from "@/components/right-sidebar/elements.tsx";
import { useRightSidebar } from "@/components/right-sidebar/hooks.tsx";
import { AvatarWithFallback } from "@/components/ui/avatar.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Card } from "@/components/ui/card.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { ErrorMessage } from "@/components/common/Error.tsx";
import { SidebarContent } from "@/components/ui/sidebar.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { useContactProfilesEnabled } from "@/hooks/useContactProfilesEnabled.ts";
import {
  useAddContactHandle,
  useContact,
  useDeleteContactHandle,
  useSetPreferredHandle,
  useUpdateContact,
} from "@/hooks/useContacts.tsx";
import { cn } from "@/lib/utils.ts";
import { channelLabel, isHttpUrl, relationshipTags, urlLabel } from "@/shared/contactProfile.ts";
import { type Contact, contactTypeTag, type Handle } from "@/types/contact.ts";
import { useForm } from "@tanstack/react-form";
import { format } from "date-fns";
import { ExternalLink, Mail, MapPin, Pencil, Phone, Plus, Star, Trash2 } from "lucide-react";
import { listTimezones } from "@/shared/timezones.ts";
import { useState } from "react";
import { ContactActivityTabs } from "./ContactActivityTabs.tsx";
import { ContactDeleteDialog } from "./ContactDeleteDialog.tsx";
import { useContactProfile } from "./useContactActivity.ts";

/** Computed once at module load; the same set native's picker offers. */
const TIMEZONES: string[] = listTimezones();

const handleIcon = (type: Handle["type"]) => {
  switch (type) {
    case "emailAddress":
      return <Mail className="size-3.5 shrink-0 text-muted-foreground" />;
    case "phoneNumber":
      return <Phone className="size-3.5 shrink-0 text-muted-foreground" />;
    case "address":
      return <MapPin className="size-3.5 shrink-0 text-muted-foreground" />;
  }
};

/** Absolute "Jul 18, 2026, 3:30 PM"; invalid/absent timestamps render nothing. */
const formatDateTime = (iso: string): string | null => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : format(date, "PPp");
};

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
    {children}
  </h4>
);

/** A label/value row for the scalar "Details" section. Renders nothing when empty. */
const DetailRow = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm text-right min-w-0 truncate">{value}</span>
    </div>
  ) : null;

/**
 * Inline editor for a contact's base fields (name, nickname, timezone) plus its
 * handles. Name/nickname/timezone are saved together on Save via
 * {@link useUpdateContact}; handle add / remove / set-preferred are separate
 * endpoints and apply immediately. Only rendered for editable (non user/assistant)
 * contacts — the caller gates on that.
 */
const ContactDetailEditForm = ({
  contact,
  onDone,
  onDeleted,
}: {
  contact: Contact;
  onDone: () => void;
  onDeleted: () => void;
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const updateContact = useUpdateContact(contact.id);
  const addHandle = useAddContactHandle(contact.id);
  const setPreferred = useSetPreferredHandle(contact.id);
  const deleteHandle = useDeleteContactHandle(contact.id);

  const [newHandleType, setNewHandleType] = useState<"emailAddress" | "phoneNumber">(
    "emailAddress",
  );
  const [newHandleValue, setNewHandleValue] = useState("");

  const form = useForm({
    defaultValues: {
      name: contact.name ?? "",
      nickname: contact.nickname ?? "",
      currentTimezone: contact.currentTimezone ?? "",
    },
    onSubmit: async ({ value }) => {
      // Flush a handle typed into the add row but not yet committed via "Add",
      // so Save persists everything the user entered rather than dropping it.
      if (newHandleValue.trim()) {
        await onAddHandle();
      }
      await updateContact.mutateAsync({
        name: value.name.trim(),
        nickname: value.nickname.trim(),
        currentTimezone: value.currentTimezone.trim(),
      });
      onDone();
    },
  });

  const handles = contact.handles ?? [];

  const onAddHandle = async () => {
    const value = newHandleValue.trim();
    if (!value) return;
    await addHandle.mutateAsync({ type: newHandleType, value });
    setNewHandleValue("");
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      className="flex flex-col gap-4"
    >
      <form.Field name="name">
        {(field) => (
          <div className="space-y-1.5">
            <Label htmlFor="contact-name">Name</Label>
            <Input
              id="contact-name"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder="Full name"
            />
          </div>
        )}
      </form.Field>

      <form.Field name="nickname">
        {(field) => (
          <div className="space-y-1.5">
            <Label htmlFor="contact-nickname">Nickname</Label>
            <Input
              id="contact-nickname"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder="What they like to be called"
            />
          </div>
        )}
      </form.Field>

      <form.Field name="currentTimezone">
        {(field) => (
          <div className="space-y-1.5">
            <Label htmlFor="contact-timezone">Timezone</Label>
            <Select
              value={field.state.value || "none"}
              onValueChange={(value) => field.handleChange(value === "none" ? "" : value)}
            >
              <SelectTrigger id="contact-timezone">
                <SelectValue placeholder="Select a timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </form.Field>

      <Separator />

      {/* Handles editor: preferred + delete per row, plus an add row. */}
      <div className="flex flex-col gap-2">
        <SectionHeader>Contact Info</SectionHeader>
        {handles.map((h) => (
          <div key={h.id} className="flex items-center gap-2">
            {handleIcon(h.type)}
            <span className="text-sm truncate flex-1">{h.value}</span>
            <Button
              type="button"
              variant="ghost"
              size="action"
              className={cn(
                "shrink-0",
                h.isPreferred ? "text-yellow-500" : "text-muted-foreground",
              )}
              onClick={() => setPreferred.mutate(h.id)}
              disabled={h.isPreferred || setPreferred.isPending}
              aria-label={h.isPreferred ? "Preferred contact method" : "Set as preferred"}
              title={h.isPreferred ? "Preferred contact method" : "Set as preferred"}
              data-statsig-name="contact-edit-set-preferred-handle"
            >
              <Star className={cn("size-3.5", h.isPreferred && "fill-current")} />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="action"
              className="shrink-0 text-muted-foreground"
              onClick={() => deleteHandle.mutate(h.id)}
              disabled={deleteHandle.isPending}
              aria-label="Remove contact method"
              title="Remove contact method"
              data-statsig-name="contact-edit-delete-handle"
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={newHandleType}
            onValueChange={(value: "emailAddress" | "phoneNumber") => setNewHandleType(value)}
          >
            <SelectTrigger className="w-28 shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="emailAddress">Email</SelectItem>
              <SelectItem value="phoneNumber">Phone</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={newHandleValue}
            onChange={(e) => setNewHandleValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onAddHandle();
              }
            }}
            type={newHandleType === "emailAddress" ? "email" : "tel"}
            placeholder={newHandleType === "emailAddress" ? "name@example.com" : "+1 234 567 8900"}
            className="min-w-[12rem] flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAddHandle}
            disabled={!newHandleValue.trim() || addHandle.isPending}
            data-statsig-name="contact-edit-add-handle"
          >
            <Plus className="size-3.5" />
            Add
          </Button>
        </div>
      </div>

      <Separator />

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => setIsDeleteDialogOpen(true)}
          disabled={updateContact.isPending}
          data-statsig-name="contact-edit-delete"
        >
          <Trash2 className="size-4" />
          Delete
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onDone}
          disabled={updateContact.isPending}
          data-statsig-name="contact-edit-cancel"
        >
          Cancel
        </Button>
        <form.Subscribe selector={(state) => [state.values.name, state.isSubmitting] as const}>
          {([name, isSubmitting]) => (
            <Button
              type="submit"
              size="sm"
              disabled={!name.trim() || isSubmitting}
              data-statsig-name="contact-edit-save"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          )}
        </form.Subscribe>
      </div>
      {/* Portaled out of the form, so its buttons cannot submit it. */}
      <ContactDeleteDialog
        contact={contact}
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDeleted={onDeleted}
      />
    </form>
  );
};

/**
 * The contact-detail content — avatar/handles/location card plus the activity
 * tabs — with no surrounding chrome. Rendered by {@link ContactDetailSidebar},
 * the canvas contact overlay, and the full contact-detail page; each host supplies
 * its own navigation affordances, header, and padding.
 */
export const ContactDetailBody = ({
  contactId,
  defaultTab,
  onDeleted,
}: {
  contactId: string;
  defaultTab?: "communications" | "events" | "bio";
  /** Leaves the contact after it is deleted from the edit form; each host navigates its own way. */
  onDeleted: () => void;
}) => {
  const { contact, isLoading, error } = useContact(contactId);
  // The research-backed profile carries nickname, relationship tags, company/title,
  // preferred channel, and last-interaction. Gated + 404-tolerant (null when absent),
  // and deduped with the Bio tab's fetch (same query key).
  const { enabled: profilesEnabled } = useContactProfilesEnabled();
  const { data: profile } = useContactProfile(contactId, profilesEnabled);
  // Track edit mode by the contact being edited rather than a bare boolean, so that
  // switching contacts implicitly leaves edit mode (`isEditing` is derived, not an
  // effect). Otherwise the form — which initializes its scalar fields once from the
  // mounted contact — would save contact A's name/nickname/timezone onto contact B.
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const isEditing = editingContactId === contactId;

  if (isLoading) return <Loader />;
  if (error || !contact) return <ErrorMessage error={error ?? new Error("Contact not found")} />;

  const handles = contact.handles ?? [];
  const displayName = contact.name || "Unnamed";
  const typeTag = contactTypeTag(contact.type);
  // The user's explicit nickname edit wins; fall back to the research profile's preferred name.
  const nickname = contact.nickname || profile?.preferredName;
  // User + assistant contacts are managed by the system, not hand-edited.
  const isEditable = contact.type !== "user" && contact.type !== "assistant";

  // Relationship tags (Importance / Temperature / Type) rendered as chips.
  const tags = relationshipTags(profile);

  const profileUrls = (profile?.profileUrls ?? []).filter(isHttpUrl);
  const lastInteraction = profile?.lastInteractionAt
    ? formatDateTime(profile.lastInteractionAt)
    : null;
  const hasDetails = Boolean(
    profile?.companyName ||
    profile?.role ||
    contact.currentTimezone ||
    profile?.preferredChannel ||
    lastInteraction,
  );

  return (
    <>
      <Card className="p-4 shadow-none">
        {isEditing ? (
          <ContactDetailEditForm
            contact={contact}
            onDone={() => setEditingContactId(null)}
            onDeleted={onDeleted}
          />
        ) : (
          <>
            {/* Header: avatar + name + nickname + type badge */}
            <div className="flex items-center gap-3">
              <AvatarWithFallback
                size="xl"
                person={{
                  name: displayName,
                  picture: contact.picture,
                  value: contact.id,
                }}
              />
              <div className="flex flex-col min-w-0 flex-1">
                <h3 className="font-semibold text-lg truncate">{displayName}</h3>
                {nickname && nickname !== displayName && (
                  <span className="text-sm text-muted-foreground truncate">“{nickname}”</span>
                )}
                {typeTag && (
                  <Badge variant="secondary" className="text-xs w-fit mt-0.5">
                    {typeTag}
                  </Badge>
                )}
              </div>
              {isEditable && (
                <Button
                  variant="ghost"
                  size="action"
                  className="shrink-0 self-start text-muted-foreground"
                  onClick={() => setEditingContactId(contactId)}
                  aria-label="Edit contact"
                  data-statsig-name="contact-edit-open"
                >
                  <Pencil className="size-4" />
                </Button>
              )}
            </div>

            {/* Relationship Tags */}
            {tags.length > 0 && (
              <>
                <Separator className="my-4" />
                <div className="flex flex-col">
                  <SectionHeader>Relationship</SectionHeader>
                  <div className="flex flex-wrap gap-1">
                    {tags.map((tag) => (
                      <Badge key={tag.label} variant="secondary" size="sm">
                        {tag.label}: {tag.value}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Contact Info: handles + profile URLs */}
            {(handles.length > 0 || profileUrls.length > 0) && (
              <>
                <Separator className="my-4" />
                <div className="flex flex-col">
                  <SectionHeader>Contact Info</SectionHeader>
                  <div className="flex flex-col divide-y">
                    {handles.map((h) => (
                      <div key={h.id} className="flex items-center gap-2.5 py-2">
                        {handleIcon(h.type)}
                        <span className="text-sm truncate">{h.value}</span>
                        {h.isPreferred && (
                          <Badge variant="secondary" size="xs">
                            Preferred
                          </Badge>
                        )}
                      </div>
                    ))}
                    {profileUrls.map((url) => (
                      <a
                        key={url}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2.5 py-2 hover:underline"
                      >
                        <ExternalLink className="size-3.5 shrink-0 text-muted-foreground" />
                        <span className="text-sm truncate">{urlLabel(url)}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Location */}
            {contact.locations?.length ? (
              <>
                <Separator className="my-4" />
                <div className="flex flex-col">
                  <SectionHeader>Location</SectionHeader>
                  <div className="flex flex-col divide-y">
                    {contact.locations.map((loc) => (
                      <div
                        key={`${loc.type}-${loc.value}`}
                        className="flex items-center gap-2.5 py-2"
                      >
                        <MapPin className="size-3.5 shrink-0 text-muted-foreground" />
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm truncate">{loc.value}</span>
                          {loc.timezoneId && (
                            <span className="text-xs text-muted-foreground truncate">
                              {loc.timezoneName || loc.timezoneId}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            {/* Details: company, title, timezone, preferred channel, last interaction */}
            {hasDetails && (
              <>
                <Separator className="my-4" />
                <div className="flex flex-col">
                  <SectionHeader>Details</SectionHeader>
                  <div className="flex flex-col divide-y">
                    <DetailRow label="Company" value={profile?.companyName} />
                    <DetailRow label="Title" value={profile?.role} />
                    <DetailRow label="Timezone" value={contact.currentTimezone} />
                    <DetailRow
                      label="Preferred Channel"
                      value={
                        profile?.preferredChannel ? channelLabel[profile.preferredChannel] : null
                      }
                    />
                    <DetailRow label="Last Interaction" value={lastInteraction} />
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </Card>
      <ContactActivityTabs contactId={contact.id} defaultTab={defaultTab} />
    </>
  );
};

/**
 * Right-sidebar host for a single contact's detail: the shared
 * {@link ContactDetailBody} wrapped in sidebar chrome (header + close).
 *
 * The canvas an activity row presents into is mounted a level out, on
 * `ContactListSidebar` — the sidebar's own frame. It has to be: the canvas
 * stack renders this file's body, so a canvas host imported here would close
 * the import cycle that runs back through `CanvasContactModal`.
 */
export const ContactDetailSidebar = ({
  contactId,
  defaultTab,
}: {
  contactId: string;
  defaultTab?: "communications" | "events" | "bio";
}) => {
  const { closeSidebar } = useRightSidebar();
  const { contact } = useContact(contactId);
  const title = contact?.name || "Contact";

  return (
    <>
      <RightSidebarHeader title={title} handleClose={closeSidebar} />
      <SidebarContent className="px-4 py-4">
        <ContactDetailBody contactId={contactId} defaultTab={defaultTab} onDeleted={closeSidebar} />
      </SidebarContent>
    </>
  );
};
